/** Practice Center intro card — "what are you preparing for" + the next Teil
 * to tackle, with a one-tap way to update the goal. Answers the client's
 * "ask some steps like Viobean: check status, what are you preparing for"
 * request, reusing the onboarding answers we already hold on the user. */
import { Link } from "react-router-dom";
import type { ExamOverview } from "../../api/exams";
import type { OnboardingAnswers } from "../../api/types";
import { Button, Card } from "../../components/ui";
import { useT } from "../../i18n";
import type { MessageKey } from "../../i18n/en";

const REASON_KEY: Record<string, MessageKey> = {
  exam: "practice.status.goal.exam",
  work: "practice.status.goal.work",
  study: "practice.status.goal.study",
  immigration: "practice.status.goal.immigration",
  travel: "practice.status.goal.travel",
};

/** First Teil in the exam that still has unpractised items — the natural
 * "next up". Returns null when everything is done (or the exam is absent). */
export function nextUnpractised(exam: ExamOverview | undefined) {
  if (!exam) return null;
  for (const section of exam.sections) {
    for (const part of section.parts) {
      if (part.item_count > 0 && part.practiced < part.item_count) {
        return { section, part };
      }
    }
  }
  return null;
}

export function PracticeStatusCard({
  onboarding,
  exam,
}: {
  onboarding?: OnboardingAnswers;
  exam?: ExamOverview;
}) {
  const t = useT();
  const goal = onboarding?.goal;
  const board = onboarding?.board;
  const targetLevel = onboarding?.target_level ?? onboarding?.level;

  if (!targetLevel) {
    return (
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted">{t("practice.status.noGoal")}</p>
          <Link to="/onboarding">
            <Button variant="secondary">{t("practice.status.setGoal")}</Button>
          </Link>
        </div>
      </Card>
    );
  }

  const boardLabel = board === "telc" ? "telc" : board === "goethe" ? "Goethe" : null;
  const prep = [boardLabel, targetLevel].filter(Boolean).join(" ");
  const reasonKey = goal ? REASON_KEY[goal] : undefined;
  const next = nextUnpractised(exam);

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-muted">
            {t("practice.status.preparingFor")}
          </p>
          <p className="text-xl font-extrabold text-ink">{prep}</p>
          {reasonKey && <p className="mt-0.5 text-sm text-muted">{t(reasonKey)}</p>}
        </div>
        <Link to="/onboarding">
          <Button variant="secondary">{t("practice.status.updateGoal")}</Button>
        </Link>
      </div>
      {next && exam && (
        <Link
          to={`/practice/${exam.board}/${exam.level}/${next.part.code}`}
          className="mt-4 flex items-center justify-between rounded-control bg-canvas p-3 text-sm hover:bg-brand-soft"
        >
          <span>
            <span className="font-semibold text-ink">{t("practice.status.nextUp")}:</span>{" "}
            <span className="text-muted">
              {next.section.name} · {next.part.title}
            </span>
          </span>
          <span aria-hidden className="text-brand">
            →
          </span>
        </Link>
      )}
    </Card>
  );
}
